import React from 'react';
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  TextField,
  Box,
  Typography,
  Chip,
  Avatar,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CircularProgress,
  Paper,
} from '@mui/material';
import { Search, Clear, Check } from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledAutocomplete = styled(MuiAutocomplete, {
  shouldForwardProp: (prop) => prop !== 'variant',
})<{ variant?: 'default' | 'outlined' | 'filled' }>(
  ({ theme, variant = 'default' }) => ({
    '& .MuiAutocomplete-inputRoot': {
      ...(variant === 'outlined' && {
        borderRadius: `calc(${theme.shape.borderRadius}px * 1.5)`,
      }),
      ...(variant === 'filled' && {
        backgroundColor: theme.palette.grey[100],
        '&:hover': {
          backgroundColor: theme.palette.grey[200],
        },
        '&.Mui-focused': {
          backgroundColor: theme.palette.background.paper,
        },
      }),
    },
    '& .MuiAutocomplete-option': {
      padding: theme.spacing(1, 2),
      '&[aria-selected="true"]': {
        backgroundColor: theme.palette.primary.main + '10',
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    '& .MuiAutocomplete-popupIndicator': {
      color: theme.palette.text.secondary,
    },
    '& .MuiAutocomplete-clearIndicator': {
      color: theme.palette.text.secondary,
    },
  })
);

const CustomListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  margin: 0,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&[aria-selected="true"]': {
    backgroundColor: theme.palette.primary.main + '10',
    '&:hover': {
      backgroundColor: theme.palette.primary.main + '20',
    },
  },
}));

const HighlightedText = styled('span')(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

export interface OptionData {
  /**
   * Unique identifier
   */
  id: string | number;
  /**
   * Display label
   */
  label: string;
  /**
   * Optional description
   */
  description?: string;
  /**
   * Optional avatar/image URL
   */
  avatar?: string;
  /**
   * Optional icon
   */
  icon?: React.ReactNode;
  /**
   * Optional category
   */
  category?: string;
  /**
   * Whether option is disabled
   */
  disabled?: boolean;
  /**
   * Additional metadata
   */
  metadata?: Record<string, any>;
}

export interface AutocompleteProps<T extends OptionData> {
  /**
   * Array of options
   */
  options: T[];
  /**
   * Current value
   */
  value?: T | T[] | null;
  /**
   * Callback fired when value changes
   */
  onChange?: (value: T | T[] | null) => void;
  /**
   * Input label
   */
  label?: string;
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Helper text
   */
  helperText?: string;
  /**
   * Whether field has error
   */
  error?: boolean;
  /**
   * Whether field is required
   */
  required?: boolean;
  /**
   * Visual variant
   */
  variant?: 'default' | 'outlined' | 'filled';
  /**
   * Size of the component
   */
  size?: 'small' | 'medium';
  /**
   * Whether to allow multiple selections
   */
  multiple?: boolean;
  /**
   * Whether component is in loading state
   */
  loading?: boolean;
  /**
   * Whether to show avatars in options
   */
  showAvatars?: boolean;
  /**
   * Whether to show descriptions in options
   */
  showDescriptions?: boolean;
  /**
   * Whether to highlight matching text
   */
  highlightMatches?: boolean;
  /**
   * Whether to group options by category
   */
  groupByCategory?: boolean;
  /**
   * Custom loading text
   */
  loadingText?: string;
  /**
   * Custom no options text
   */
  noOptionsText?: string;
  /**
   * Whether to allow creation of new options
   */
  allowCreate?: boolean;
  /**
   * Callback for creating new options
   */
  onCreateOption?: (inputValue: string) => T;
  /**
   * Custom chip props for multiple selection
   */
  chipProps?: any;
  /**
   * Maximum number of selections (for multiple)
   */
  maxSelections?: number;
  /**
   * Whether to show selected indicator
   */
  showSelectedIndicator?: boolean;
  /**
   * Custom filter function
   */
  filterOptions?: (options: T[], params: any) => T[];
  /**
   * Function to group options
   */
  groupBy?: (option: T) => string;
  /**
   * Async function to load options
   */
  loadOptions?: (inputValue: string) => Promise<T[]>;
}

const highlightText = (text: string, highlight: string) => {
  if (!highlight) return text;
  
  const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
  return parts.map((part, index) =>
    part.toLowerCase() === highlight.toLowerCase() ? (
      <HighlightedText key={index}>{part}</HighlightedText>
    ) : (
      part
    )
  );
};

const getOptionLabel = (option: OptionData | string) => {
  if (typeof option === 'string') return option;
  return option.label;
};

const isOptionEqualToValue = (option: OptionData, value: OptionData) => {
  return option.id === value.id;
};

export const Autocomplete = <T extends OptionData>({
  options,
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error = false,
  required = false,
  variant = 'default',
  size = 'medium',
  showAvatars = false,
  showDescriptions = false,
  highlightMatches = true,
  groupByCategory = false,
  loadingText = 'Loading...',
  noOptionsText = 'No options',
  allowCreate = false,
  onCreateOption,
  chipProps = {},
  maxSelections,
  showSelectedIndicator = true,
  filterOptions,
  loadOptions,
  multiple = false,
  loading = false,
  ...props
}: AutocompleteProps<T>) => {
  const [inputValue, setInputValue] = React.useState('');
  const [asyncOptions, setAsyncOptions] = React.useState<T[]>([]);
  const [asyncLoading, setAsyncLoading] = React.useState(false);

  // Handle async options loading
  React.useEffect(() => {
    if (loadOptions && inputValue) {
      setAsyncLoading(true);
      const timeoutId = setTimeout(async () => {
        try {
          const newOptions = await loadOptions(inputValue);
          setAsyncOptions(newOptions);
        } catch (error) {
          console.error('Error loading options:', error);
        } finally {
          setAsyncLoading(false);
        }
      }, 300); // Debounce

      return () => clearTimeout(timeoutId);
    }
  }, [inputValue, loadOptions]);

  const processedOptions = loadOptions ? asyncOptions : options;

  const handleChange = (event: any, newValue: any) => {
    // Handle max selections for multiple
    if (multiple && maxSelections && Array.isArray(newValue) && newValue.length > maxSelections) {
      return;
    }

    // Handle create option
    if (allowCreate && typeof newValue === 'string' && onCreateOption) {
      const createdOption = onCreateOption(newValue);
      onChange?.(multiple ? [...(value as T[] || []), createdOption] : createdOption);
      return;
    }

    onChange?.(newValue);
  };

  const renderOption = (props: any, option: T, { inputValue: searchValue }: any) => {
    const isSelected = multiple 
      ? (value as T[])?.some(v => isOptionEqualToValue(option, v))
      : value && isOptionEqualToValue(option, value as T);

    return (
      <CustomListItem {...props} key={option.id}>
        {showAvatars && (
          <ListItemAvatar>
            <Avatar 
              src={option.avatar} 
              sx={{ width: 32, height: 32 }}
            >
              {option.icon || option.label.charAt(0)}
            </Avatar>
          </ListItemAvatar>
        )}
        
        <ListItemText
          primary={
            highlightMatches ? 
              highlightText(option.label, searchValue) : 
              option.label
          }
          secondary={
            showDescriptions && option.description ? (
              highlightMatches ? 
                highlightText(option.description, searchValue) : 
                option.description
            ) : undefined
          }
        />
        
        {showSelectedIndicator && isSelected && (
          <Check color="primary" fontSize="small" />
        )}
      </CustomListItem>
    );
  };

  const renderTags = (tagValue: T[], getTagProps: any) => {
    const displayTags = maxSelections && tagValue.length > maxSelections
      ? tagValue.slice(0, maxSelections)
      : tagValue;

    return (
      <>
        {displayTags.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.label}
            size={size === 'small' ? 'small' : 'medium'}
            avatar={showAvatars && option.avatar ? <Avatar src={option.avatar} /> : undefined}
            {...chipProps}
          />
        ))}
        {maxSelections && tagValue.length > maxSelections && (
          <Chip
            label={`+${tagValue.length - maxSelections} more`}
            size={size === 'small' ? 'small' : 'medium'}
            variant="outlined"
          />
        )}
      </>
    );
  };

  const groupBy = groupByCategory ? (option: T) => option.category || 'Other' : undefined;

  return (
    <StyledAutocomplete
      options={processedOptions}
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={getOptionLabel as any}
      isOptionEqualToValue={isOptionEqualToValue as any}
      renderOption={renderOption as any}
      renderTags={multiple ? renderTags as any : undefined}
      groupBy={groupBy as any}
      filterOptions={filterOptions as any}
      multiple={multiple}
      loading={loading || asyncLoading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      freeSolo={allowCreate}
      variant={variant}
      size={size}
      {...props as any}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          required={required}
          variant={variant === 'default' ? 'outlined' : variant}
          size={size}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Search color="action" sx={{ mr: 1 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {(loading || asyncLoading) ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      PaperComponent={({ children, ...paperProps }) => (
        <Paper {...paperProps} elevation={8}>
          {children}
        </Paper>
      )}
    />
  );
}; 